import React from 'react';
import {
  Box,
  Grid,
  GridItem,
  Input,
  Button,
  Text,
  Icon,
} from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import { FaCalendarAlt, FaFilter } from 'react-icons/fa';
import { useForm, Controller, useWatch } from 'react-hook-form';
import SelectBox from '../fields/SelectBox';
import { useColorModeValue } from '../../ui/color-mode';
import { darkMode, lightMode } from '../../../theme/config';
import COLOR from '../../../theme/colors';
import 'react-datepicker/dist/react-datepicker.css';
import { useDispatch } from 'react-redux';
import { resetFilterData, setFilterData } from '../../../store/filterSlice';

const FilterPanel = ({
  path,
  userId,
  filters = {},
  categoryOptions = [],
  subCategoryOptions = [],
  label = "Apply Filter",
  bg = COLOR.FILTER_BUTTON,
  variant = "solid",
  color = "white",
  hideDateRange,
}) => {

  const dispatch = useDispatch();
  const borderColor = useColorModeValue(lightMode.containerBorderColor, darkMode.containerBorderColor);
  const backgoundColor = useColorModeValue(lightMode.boxBackgroundColor, darkMode.boxBackgroundColor)

  const initialFromDate = filters.fromDate ? new Date(filters.fromDate) : null;
  const initialToDate = filters.toDate ? new Date(filters.toDate) : null;

  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues: {
      fromDate: initialFromDate,
      toDate: initialToDate,
      status: filters.defaultStatus ?? [],
      category: filters.defaultCategory ?? [],
      subCategory: filters.defaultSubCategory ?? [],
    },
  });

  const selectedCategory = useWatch({
    control,
    name: 'category',
  });

  React.useEffect(() => {
    setValue('subCategory', []);
  }, [selectedCategory, setValue]);

  const onSubmit = (data) => {
    const formatDate = (date) =>
      date ? date.toLocaleDateString('en-CA') : null;

    if (userId) {
      dispatch(setFilterData({
        key: path,
        data: {
          ...data,
          userId: userId,
          fromDate: formatDate(data.fromDate),
          toDate: formatDate(data.toDate),
        },
      }));
    } else {
      dispatch(setFilterData({
        key: path,
        data: {
          ...data,
          fromDate: formatDate(data.fromDate),
          toDate: formatDate(data.toDate),
        },
      }));
    }

  };

  const handleReset = () => {
    reset({
      fromDate: null,
      toDate: null,
      status: [],
      category: [],
    });
    dispatch(resetFilterData({
      key: path
    }));
  };

  return (
    <Box
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      border="1px solid"
      borderColor={borderColor}
      p={4}
      borderRadius="md"
      mb={4}
      bg={backgoundColor}
    >
      <Grid
        templateColumns={{
          base: "1fr",
          sm: "repeat(2, 1fr)",
          md: "repeat(4, 1fr)",
        }}
        gap={2}
      >
        {!hideDateRange && (
          <GridItem>
            <Text mb={1} fontWeight="semibold">
              Date Range
            </Text>
            <Box position="relative">
              <Icon
                as={FaCalendarAlt}
                position="absolute"
                left={2}
                top="50%"
                transform="translateY(-50%)"
                color="gray.400"
                pointerEvents="none"
                zIndex={10}
              />
              <Controller
                control={control}
                name="fromDate"
                render={({ field }) => (
                  <DatePicker
                    placeholderText="From date"
                    selected={field.value}
                    onChange={(date) => field.onChange(date)}
                    customInput={<Input pl="2rem" bg={backgoundColor} />}
                    maxDate={new Date()}
                    dayClassName={(date) =>
                      date > new Date() ? 'disabled-day' : undefined
                    }
                  />
                )}
              />
            </Box>
          </GridItem>
        )}

        {!hideDateRange && (
          <GridItem paddingTop="7">
            <Box position="relative">
              <Icon
                as={FaCalendarAlt}
                position="absolute"
                left={2}
                top="50%"
                transform="translateY(-50%)"
                color="gray.400"
                pointerEvents="none"
                zIndex={10}
              />
              <Controller
                control={control}
                name="toDate"
                render={({ field }) => (
                  <DatePicker
                    placeholderText="To date"
                    selected={field.value}
                    onChange={(date) => field.onChange(date)}
                    customInput={<Input pl="2rem" bg={backgoundColor} />}
                    maxDate={new Date()}
                    dayClassName={(date) =>
                      date > new Date() ? 'disabled-day' : undefined
                    }
                  />
                )}
              />
            </Box>
          </GridItem>
        )}

        <GridItem>
          <SelectBox
            label="Category"
            name="category"
            control={control}
            items={categoryOptions}
            placeholder="All Categories"
          />
        </GridItem>

        <GridItem>
          <SelectBox
            label="Sub Category"
            name="subCategory"
            control={control}
            disabled={!selectedCategory || selectedCategory.length === 0}
            items={
              selectedCategory
                ? subCategoryOptions[selectedCategory] || []
                : []
            }
            placeholder="All Sub Categories"
          />
        </GridItem>

        {hideDateRange && <GridItem></GridItem>}

        {!hideDateRange && (
          <>
            <GridItem />
            <GridItem />
            <GridItem />
          </>
        )}

        <GridItem>
          <Box
            display="flex"
            gap={2}
            paddingTop={hideDateRange ? "7" : "0"}
            justifyContent="flex-end"
            mt="auto"
          >
            <Button variant={variant} color={color} bg={bg} type="submit">
              <FaFilter
                style={{
                  fill: "none",
                  stroke: "currentColor",
                  strokeWidth: 30,
                }}
              />
              {label}
            </Button>
            <Button variant="outline" fontWeight="bold" bg={backgoundColor} onClick={handleReset}>
              Reset
            </Button>
          </Box>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default FilterPanel;
